"use client";

import { Box, Text, Title, Stack, Group, Paper, UnstyledButton, Collapse } from "@mantine/core";
import { IconChevronDown, IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { useState, useEffect, useCallback, ReactNode } from "react";
import { setNavigationProgress } from "@mantine/nprogress";

// ============================================
// TYPES
// ============================================

export interface QuizOption {
  label: string;
  value: string;
  color?: string; // Optional color swatch
}

export interface QuizQuestion {
  id: string;
  title: string;
  description: string;
  helpText?: string;
  options: QuizOption[];
}

interface TypeformQuizProps {
  questions: QuizQuestion[];
  onComplete: (answers: Record<string, string>) => void;
  onExit?: () => void;
  accentColor?: string;
  sharedContainer?: boolean;
  labels?: { back: string; help: string; keyboard: string; select: string };
  showHelpText?: boolean; // Default collapsed
}

// ============================================
// ANIMATIONS
// ============================================

const animations = `
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideOutUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-30px);
  }
}

@keyframes optionPop {
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
}

@keyframes checkmark {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes progressPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
`;

// ============================================
// MAIN COMPONENT
// ============================================

export default function TypeformQuiz({
  questions,
  onComplete,
  onExit,
  accentColor = "#7C3AED", // violet
  showHelpText = false,
  sharedContainer = false,
  labels,
}: TypeformQuizProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<"forward" | "backward">("forward");
  const [helpExpanded, setHelpExpanded] = useState(showHelpText);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const currentAnswer = answers[currentQuestion.id];

  // Update progress bar
  useEffect(() => {
    const progress = ((currentIndex + (currentAnswer ? 1 : 0)) / questions.length) * 100;
    setNavigationProgress(progress);
  }, [currentIndex, currentAnswer, questions.length]);

  // Reset help text expansion when question changes
  useEffect(() => {
    setHelpExpanded(showHelpText);
  }, [currentIndex, showHelpText]);

  const handleSelect = useCallback((value: string) => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSelectedOption(value);
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));

    // Brief delay to show selection feedback, then advance
    setTimeout(() => {
      setIsTransitioning(true);
      setTransitionDirection("forward");

      setTimeout(() => {
        if (isLastQuestion) {
          const finalAnswers = { ...answers, [currentQuestion.id]: value };
          onComplete(finalAnswers);
        } else {
          setCurrentIndex(prev => prev + 1);
          setSelectedOption(null);
        }
        setIsTransitioning(false);
      }, 300);
    }, 200);
  }, [isTransitioning, currentQuestion.id, isLastQuestion, answers, onComplete]);

  const handleBack = useCallback(() => {
    if (isTransitioning) return;

    if (currentIndex === 0) {
      onExit?.();
      return;
    }

    setIsTransitioning(true);
    setTransitionDirection("backward");

    setTimeout(() => {
      setCurrentIndex(prev => prev - 1);
      setSelectedOption(null);
      setIsTransitioning(false);
    }, 300);
  }, [currentIndex, isTransitioning, onExit]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTransitioning) return;

      // Number keys 1-9 for options
      const num = parseInt(e.key);
      if (num >= 1 && num <= currentQuestion.options.length) {
        handleSelect(currentQuestion.options[num - 1].value);
      }

      // Escape to go back
      if (e.key === "Escape") {
        handleBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentQuestion.options, handleSelect, handleBack, isTransitioning]);

  return (
    <Box
      style={{
        position: "fixed",
        inset: 0,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: animations }} />

      {/* Header with progress */}
      <Box
        style={{
          padding: sharedContainer ? "1rem 16px" : "1rem 1.5rem",
          maxWidth: sharedContainer ? 1320 : undefined,
          width: "100%",
          margin: "0 auto",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {/* Back button */}
        <UnstyledButton
          onClick={handleBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            color: "#666",
            fontSize: "0.875rem",
          }}
        >
          <IconArrowLeft size={18} />
          <Text visibleFrom="sm">{labels?.back || (currentIndex === 0 ? "Exit" : "Back")}</Text>
        </UnstyledButton>

        {/* Progress indicator */}
        <Box style={{ flex: 1, display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {questions.map((_, i) => (
            <Box
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: i < currentIndex ? accentColor : i === currentIndex ? accentColor : "#e5e5e5",
                opacity: i === currentIndex ? 0.6 : 1,
                transition: "all 0.3s ease",
                animation: i === currentIndex ? "progressPulse 1.5s ease-in-out infinite" : undefined,
              }}
            />
          ))}
        </Box>

        {/* Question counter */}
        <Text size="sm" c="dimmed">
          {currentIndex + 1}/{questions.length}
        </Text>
      </Box>

      {/* Main content area */}
      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: sharedContainer ? "2rem 16px" : "2rem 1.5rem",
          maxWidth: sharedContainer ? 1320 : 600,
          margin: "0 auto",
          width: "100%",
          overflow: "auto",
        }}
      >
        <Box
          key={currentIndex}
          style={{
            animation: isTransitioning
              ? transitionDirection === "forward"
                ? "slideOutUp 0.3s ease-out forwards"
                : "slideOutUp 0.3s ease-out forwards"
              : "slideInUp 0.4s ease-out",
          }}
        >
          {/* Question */}
          <Stack gap="xl" style={sharedContainer ? { width: "100%" } : undefined}>
            <Box>
              <Title
                order={2}
                fz={{ base: "1.5rem", md: "1.75rem" }}
                fw={600}
                style={{ color: "#1a1a1a", lineHeight: 1.3 }}
              >
                {currentQuestion.description}
              </Title>
            </Box>

            {/* Options */}
            <Stack gap="sm" style={sharedContainer ? { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 440px), 1fr))" } : undefined}>
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedOption === option.value || currentAnswer === option.value;
                const letter = String.fromCharCode(65 + index); // A, B, C...

                return (
                  <UnstyledButton
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    disabled={isTransitioning}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "1rem 1.25rem",
                      borderRadius: 12,
                      border: `2px solid ${isSelected ? accentColor : "#e5e5e5"}`,
                      background: isSelected ? `${accentColor}10` : "#fff",
                      transition: "all 0.2s ease",
                      cursor: isTransitioning ? "not-allowed" : "pointer",
                      animation: isSelected ? "optionPop 0.3s ease" : undefined,
                    }}
                    onMouseEnter={(e) => {
                      if (!isTransitioning && !isSelected) {
                        e.currentTarget.style.borderColor = "#ccc";
                        e.currentTarget.style.background = "#fafafa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isTransitioning && !isSelected) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                        e.currentTarget.style.background = "#fff";
                      }
                    }}
                  >
                    {/* Letter indicator or checkmark */}
                    <Box
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        border: `2px solid ${isSelected ? accentColor : "#ddd"}`,
                        background: isSelected ? accentColor : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isSelected ? (
                        <IconCheck
                          size={18}
                          color="#fff"
                          style={{ animation: "checkmark 0.3s ease" }}
                        />
                      ) : (
                        <Text size="sm" fw={600} c="dimmed">
                          {letter}
                        </Text>
                      )}
                    </Box>

                    {/* Option content */}
                    <Group gap="xs" style={{ flex: 1 }}>
                      {option.color && (
                        <Box
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            background: option.color,
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                      )}
                      <Text
                        fw={isSelected ? 600 : 500}
                        style={{ color: isSelected ? accentColor : "#333" }}
                      >
                        {option.label}
                      </Text>
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>

            {/* Help text (collapsible) */}
            {currentQuestion.helpText && (
              <Box>
                <UnstyledButton
                  onClick={() => setHelpExpanded(!helpExpanded)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    color: "#666",
                    fontSize: "0.875rem",
                  }}
                >
                  <Text size="sm" c="dimmed">
                    {labels?.help || "Need help deciding?"}
                  </Text>
                  <IconChevronDown
                    size={16}
                    style={{
                      transform: helpExpanded ? "rotate(180deg)" : "rotate(0)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </UnstyledButton>
                <Collapse in={helpExpanded}>
                  <Paper
                    p="md"
                    mt="sm"
                    radius="md"
                    style={{ background: "#f9f9f9", border: "1px solid #eee" }}
                  >
                    <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                      {currentQuestion.helpText.replace(/\*\*(.*?)\*\*/g, "$1")}
                    </Text>
                  </Paper>
                </Collapse>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Footer hint */}
      <Box
        style={{
          padding: "1rem",
          textAlign: "center",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <Text size="xs" c="dimmed">
          {labels?.keyboard || <>Press <strong>1-{Math.min(currentQuestion.options.length, 9)}</strong> to select · <strong>Esc</strong> to go back</>}
        </Text>
      </Box>
    </Box>
  );
}
